import React from 'react';
import styled from 'styled-components';
import type { FileMeta } from '/imports/types';
import { fileHref } from '/imports/api/files';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Modal, ModalStyled } from '/imports/ui/Modal';

const FileViewerModalStyled = styled(ModalStyled)`
  /* A viewer earns the screen: nearly all of it on a desktop, all of it on a phone, where 90% would
     leave a useless sliver of overlay on each side. */
  .modal-window { width: 90vw; max-width: 100%; }
  ${css.mobileMedia} {
    .modal-window { width: 100vw; padding: 1rem; }
  }
  .modal-body { margin: 0.75rem 0 0; }
  .viewer { display: flex; align-items: center; justify-content: center; background: ${css.grey9}; border: 1px solid ${css.grey7}; border-radius: ${css.borderRadius}; }
  .viewer > * { max-width: 100%; }
  .viewer img { max-height: 70vh; object-fit: contain; }
  .viewer video { max-height: 70vh; width: 100%; }
  .viewer audio { width: 100%; margin: 1.5rem; }
  .viewer embed, .viewer iframe { width: 100%; height: 70vh; border: none; background: ${css.white}; }
  .viewer-meta { display: flex; align-items: baseline; gap: 0.5rem; margin-top: 0.5rem; font-size: 0.8rem; color: ${css.grey4}; }
  .viewer-meta .dl { margin-left: auto; }
`;

type ViewerKind = 'image' | 'video' | 'audio' | 'pdf' | 'text';

/**
 * How a file can be shown in the page, or undefined when the browser has nothing to render it with and it
 * should simply download. Driven by the stored mime type, which is what the upload recorded.
 * @param type - the file's mime type
 * @returns the viewer to use, or undefined to download instead
 */
export const fileViewerKind = (type?: string): ViewerKind | undefined => {
  const t = (type || '').toLowerCase().trim();
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  if (t === 'application/pdf') return 'pdf';
  // Anything textual the browser lays out on its own. text/html lands here too and is the reason the text
  // viewer is a SANDBOXED iframe: /nfs is same-origin, so an uploaded page in a plain iframe could script
  // against this one. An empty sandbox gives it a unique origin with scripts off.
  if (t.startsWith('text/') || t === 'application/json' || t === 'application/xml') return 'text';
  return undefined;
};

/** Whether this file opens in the viewer rather than downloading. */
export const fileIsViewable = (file?: { type?: string } | null): boolean => !!fileViewerKind(file?.type);

export interface FileViewerModalProps {
  file: FileMeta;
  title?: string;
  // What the download should be called. Without one the stored name stands, which for an uploaded file is
  // the name it arrived under and for a generated one is a content hash.
  downloadName?: string;
  onClose(): void;
}

/** Shows an image, video, audio, PDF or text file in a modal, with a download link as the way out. */
export const FileViewerModal = (props: FileViewerModalProps) => {
  const { file } = props;
  const href = fileHref(file, props.downloadName);
  const kind = fileViewerKind(file.type);
  return (
    <Modal styled={FileViewerModalStyled} title={props.title || file.name} onClose={props.onClose}>
      <div className="viewer">
        {kind === 'image' && <img src={href} alt={file.name} />}
        {kind === 'video' && <video src={href} controls />}
        {kind === 'audio' && <audio src={href} controls />}
        {kind === 'pdf' && <embed src={href} type="application/pdf" />}
        {kind === 'text' && <iframe src={href} sandbox="" title={file.name} />}
      </div>
      <div className="viewer-meta">
        <span>{file.type}</span>
        <a className="dl" href={href} target="_blank" rel="noreferrer" download={props.downloadName || file.name}>{strings.Download()}</a>
      </div>
    </Modal>
  );
};
